// ---------------------------------------------------------------------------------------------
// ITEM 2 — **THE `/talk` CAPTURE, RENDERED.**
//
// Her order: *"the /talk capture page (her exact words: ASK LALA in gold, 'Any spiritual question.
// Real answers. Real guidance. Real predictions.', the invitation line, the safety disclaimer), the
// form (first name, email, birth date, time with Unknown, real place picker), the review carousel,
// the animated glowing generating state, then LALA ARRIVES..."*
//
// >>> IT DECIDES NOTHING. Every verdict, every word and every stage line comes from
// `src/talkCapture.js`, which imports nothing and which a `.mjs` gate RUNS <<< — a page that
// composed its own refusal would be a second answer to *may she submit* (R28/R29), and the copy that
// drifts is the one that lets a request through the browser and has the server refuse it, which on
// this page is a paid click spent on a sentence she cannot act on.
//
// **THE PICKER IS THE FUNNEL'S OWN, NOT `BirthPlaceFields`.** That block is the FRONT DOOR's and
// carries a COUNTRY SELECT with it — a sixth field on a page whose whole ruling is that every
// required field is a lead lost (F2). `searchPlaces`/`keepsResults`/`lookupStateFor` in
// `src/lib/place-search.ts` are the shared law both landing pages already resolve a place through,
// so this is a third CALLER rather than a second picker.
import { safeSessionStorage } from "../lib/safe-storage";
import { useEffect, useRef, useState } from "react";
import {
  TALK_CAPTURE_COPY, TALK_INFLUENCE, TALK_STAGE_MS,
  talkCaptureVerdict, talkCaptureState, talkInfluenceAt, talkInfluenceLines,
} from "../talkCapture.js";
import { chartPollVerdict, CHART_POLL_MS } from "../chartGate.js";
import { TALK_TAG, writeLandingArrival } from "../landingTapMap.js";
import { spendGesture } from "../lib/locked-door";
import { TERMS_VERSION } from "../termsConsent.js";
import { writeTalkLanding } from "../talkDoor.js";
import { UnknownTimeCheck } from "./BirthFields";
// >>> R111 CAUGHT THIS ROUND IN THE ROUND THAT MADE IT, AND IT WAS RIGHT. <<< The first draft
// hand-typed `Birth date` and `Birth time` — and those are CATALOG ROWS (`form.birthDate`,
// `form.birthTime`) that every birth form on this site already resolves, because 14-B found all
// four copies of them living OUTSIDE the shared blocks in the forms that mount them.
//
// **THIS IS NOT A CONTRADICTION OF THIS PAGE'S ENGLISH-BY-DESIGN RULING (TALK-7).** That ruling is
// about the copy this round WRITES — her mark, her sub-line, her invitation, her safety line. A
// FIELD LABEL is a row this house already owns, and reading it is not translating the door: it is
// refusing to spell it a fifth time (R28/R29). The day she says translate /talk, these come free.
import { DEFAULT_LANGUAGE } from "../language.js";
import { t } from "../strings.js";
import { ReviewRail, useReviews } from "./ReviewRail";
import { searchPlaces, lookupStateFor, keepsResults, type PlaceLookupState } from "../lib/place-search";
import type { MemberSession } from "../lib/session-restore";

type PlaceRow = {
  place_name: string; state?: string; country?: string; country_code: string;
  latitude: string; longitude: string; timezone_id?: string; formatted?: string;
};

/**
 * >>> WHAT THIS FORM REALLY BUILDS IS AN ACCOUNT AND A ROOM, THROUGH THE HOUSE'S OWN TWO DOORS.
 * <<<
 *
 * `/api/auth/login` is the door that signs a member in and creates one on an address it has not seen
 * (the 1,349 members on this site who never used Google came through it), and `/api/room/create` is
 * the door every signup already walks — so this page mints NO new server door at all, and inherits
 * T2-2's consent gate, N18-1's cast, `markLeadConverted`, the funnel's own delivery and FUNNEL-4's
 * five-turn grant by EXISTING rather than by a second rail remembering them.
 *
 * **THE LANDING MARK IS F4-3's AND IS WRITTEN BEFORE THE ROOM IS ASKED FOR.** `enterRoom()` reads it
 * and lands her in the conversation instead of My Room; written afterwards, a slow create would let
 * the room's own overlay resolve first and she would arrive in the wrong place — the exact ordering
 * F4-3 measured and repaired.
 */
export function TalkCapture(
  { onMember, onArrive, onCapturing }:
  {
    onMember: (member: MemberSession) => void;
    /** >>> **HER ROOM IS BORN HERE, AND THE SHELL HAS TO LEARN IT (2026-08-25).** <<<
     *  `showOnboarding` answers `hasRoom` off `roomProfile` alone, and BREACH-1's identity guard
     *  CLEARS that slot at the sign-in one step above — correctly, because at that instant she has
     *  no room. `createRoom` in the shell sets it from the form it just submitted; this capture
     *  posts its OWN create and told the shell nothing, so `hasRoom` stayed false and **L2.6's
     *  create-room box rendered over Lala's greeting for the whole funnel cohort** — measured at
     *  coordinates: her Send control's own centre answered `SPAN.form-step-head__title`. And that
     *  box is UNDISMISSABLE off a public page (`dismissalHolds`), so she could not get past it.
     *
     *  It hands the chart it really POSTED rather than a second reading of the form (R28/R29),
     *  so what the room was made from and what the shell believes cannot disagree. OPTIONAL, so
     *  `FunnelPage`'s composition is unchanged. */
    onArrive: (chart?: {
      name: string; firstName: string; middleName: string; lastName: string;
      birthDate: string; birthTime: string; birthPeriod: "AM" | "PM";
      birthTimeUnknown: boolean; birthplace: string;
      latitude: string; longitude: string; timezoneName: string;
    }) => void;
    /** >>> HER GLOWING BOX MUST OUTLIVE THE ACCOUNT (2026-08-24). <<< This capture signs her in
     *  before it casts, so `isLoggedIn` flips mid-submit and `/talk` swapped to F4-1's door with
     *  her glow still on screen. The page asks `talkShowsCapture`; this is how it learns the
     *  second fact. OPTIONAL, so `FunnelPage`'s composition (which never swaps) is unchanged. */
    onCapturing?: (busy: boolean) => void;
  },
) {
  const [form, setForm] = useState({
    firstName: "", email: "", birthDate: "",
    birthTime: "", birthPeriod: "AM" as "AM" | "PM", birthTimeUnknown: false,
    birthplace: "", latitude: "", longitude: "", timezoneName: "",
    consent: false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [startedAt, setStartedAt] = useState(0);
  const [now, setNow] = useState(0);
  // >>> **HER WAIT LINES, FROM HER CONFIG, WITH THE COMMITTED SET AS THE FLOOR (2026-08-27).** <<<
  // Her clause is that she can add or edit these WITHOUT A BUILD, so they are fetched rather than
  // bundled. Seeded with the defaults so the screen is never blank for the beat before the read
  // lands — and a refusal simply leaves them, because an older line is better than no line on the
  // page her advertising pays for (R19). `talkInfluenceLines` is the ONE reader of that payload,
  // so the server cannot hand this component a shape the law has not approved (R28/R29).
  const [influence, setInfluence] = useState<readonly string[]>(TALK_INFLUENCE);
  useEffect(() => {
    let live = true;
    fetch("/api/talk/influence")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (live && d) setInfluence(talkInfluenceLines(d)); })
      .catch(() => { /* her committed defaults are already on screen */ });
    return () => { live = false; };
  }, []);
  const [placeRows, setPlaceRows] = useState<PlaceRow[]>([]);
  const [placeLookup, setPlaceLookup] = useState<PlaceLookupState>("idle");
  const placeAbort = useRef<AbortController | null>(null);
  // D-5's ruling: a `useState` guard is read from the render that BOUND the handler, so two taps
  // inside one frame both submit. On this page that is two accounts.
  const inFlight = useRef(false);

  const rows = useReviews(true);
  const verdict = talkCaptureVerdict(form);
  const state = talkCaptureState({ busy, error });

  const set = (k: string, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));

  // Typing REPLACES a resolved place: coordinates resolved against a town she has since edited are
  // not evidence about the town she is typing now.
  const typePlace = (value: string) =>
    setForm((f) => ({ ...f, birthplace: value, latitude: "", longitude: "", timezoneName: "" }));

  const pickPlace = (row: PlaceRow) => {
    setForm((f) => ({
      ...f,
      birthplace: row.formatted || [row.place_name, row.state, row.country].filter(Boolean).join(", "),
      latitude: row.latitude,
      longitude: row.longitude,
      // R118, unconditionally: an empty zone is the honest answer when the geocoder sent none, and
      // it is exactly when a leftover name from the last place would win over the fresh one.
      timezoneName: row.timezone_id || "",
    }));
    setPlaceRows([]);
    setPlaceLookup("idle");
  };

  // Debounced and aborted on the next keystroke. A page her advertising paid to bring a stranger to
  // may not fire a geocode per character.
  useEffect(() => {
    const typed = form.birthplace.trim();
    if (form.latitude || typed.length < 2) { setPlaceRows([]); setPlaceLookup("idle"); return; }
    const timer = setTimeout(async () => {
      placeAbort.current?.abort();
      const controller = new AbortController();
      placeAbort.current = controller;
      setPlaceLookup("searching");
      const answer = await searchPlaces<PlaceRow>(typed, { signal: controller.signal });
      // I1's ruling: an abort is a NEWER keystroke winning, so the rows are left exactly as they
      // were and the list never flickers.
      if (!keepsResults(answer)) setPlaceRows(answer.results);
      setPlaceLookup((previous) => lookupStateFor(answer, previous));
    }, 350);
    return () => clearTimeout(timer);
  }, [form.birthplace, form.latitude]);

  // >>> HER ANIMATED GENERATING STATE ROTATES ON A CLOCK THE LAW OWNS. <<< The tick is the only
  // thing here that knows about time; WHICH line it lands on is `talkStageAt`'s, so a gate runs the
  // rotation with no browser at all.
  useEffect(() => {
    if (state !== "working") return;
    const timer = setInterval(() => setNow(Date.now()), 400);
    return () => clearInterval(timer);
  }, [state]);

  async function submit(event: { preventDefault: () => void }) {
    event.preventDefault();
    if (inFlight.current) return;
    if (!verdict.ready) return;
    inFlight.current = true;
    setBusy(true);
    // >>> **HER ASK LALA FUNNEL (2026-08-24): THE WAIT MAY NOT WALK HER TO THE MEMBERSHIP PAGE.**
    // <<< She walked it live and landed on `/membership` with the create-room box over it, being
    // asked for the details she had just typed. MEASURED at coordinates (f49 --spend, BOTH
    // viewports, `/membership ("Membership")`), and the mechanism is ONBOARD-RESTORE's exactly, on
    // the one door that round never covered:
    //
    // **her submit is a REAL gesture and the chart takes about a minute.** `askedByHer` is true for
    // `GESTURE_WINDOW_MS` and the nav epoch does not move while she waits — so the first mount read
    // that answers 402 behind the glow (`/api/journal` reproduced it) is authorised to navigate,
    // and T8-3's interceptor takes her off her own funnel mid-cast.
    //
    // **SHE ASKED FOR A CHART, NOT FOR A LOCKED DOOR (R18), SO THE GESTURE IS SPENT HERE** —
    // SYNCHRONOUSLY, above every `await`, which is that round's own ruling word for word (*"React
    // runs CHILD effects before the parent's ... the spend is SYNCHRONOUS, inside `enter()`, above
    // `setPage`"*). A spend cannot reach a request already on the wire, and it does not need to:
    // what it forbids is the answer to a read nobody asked for moving her.
    //
    // **IT COSTS HER NOTHING SHE ASKED FOR.** Nothing on this page opens a gated door while the glow
    // is up, and `onArrive`'s own `goTo` bumps the epoch after it, so her landing is untouched.
    spendGesture();
    // The page owns her until this lands: told BEFORE the account exists, or the swap it prevents
    // has already happened by the time it is told (R52/R138 — position, not presence).
    onCapturing?.(true);
    setError("");
    setStartedAt(Date.now());
    setNow(Date.now());
    try {
      // 1. The account. This door creates a member on an address it has not seen.
      const signIn = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: form.email.trim(), name: form.firstName.trim() }),
      });
      const signInBody = await signIn.json().catch(() => null);
      // R39/R47: READ THE PAYLOAD, never the status code alone.
      if (!signIn.ok || (signInBody && signInBody.ok === false)) {
        throw new Error((signInBody && signInBody.message) || "");
      }
      // `SignupPage`s own pattern: the shell learns who she is from the answer rather than from a
      // reload, so the room create below travels with a session the app already knows about.
      onMember(signInBody && signInBody.member);

      // 2. F4-3's landing mark, BEFORE the room is asked for.
      writeTalkLanding(safeSessionStorage());

      // 3. The room. Its own overlay carries her from here.
      // >>> ONE CHART OBJECT: THE ONE POSTED IS THE ONE HANDED BACK (R28/R29). <<< A second
      // reading of `form` composed at `onArrive` would be a second answer to what her room was
      // made from, and the copy that drifts is the one deciding whether her greeting is covered.
      const chart = {
        name: form.firstName.trim(),
        firstName: form.firstName.trim(),
        middleName: "",
        lastName: "",
        birthDate: form.birthDate,
        birthTime: form.birthTimeUnknown ? "" : form.birthTime,
        birthPeriod: form.birthPeriod,
        birthTimeUnknown: form.birthTimeUnknown,
        birthplace: form.birthplace,
        latitude: form.latitude,
        longitude: form.longitude,
        timezoneName: form.timezoneName,
      };
      // >>> **HER ARRIVAL IS STASHED HERE, AND ITS ABSENCE IS THE LAST LINK OF TONIGHT'S DEFECT.**
      // <<< `writeLandingArrival` lives inside `openSignup`, which is "the ONE place every signup
      // trigger passes through" — and THIS CAPTURE IS NOT ONE OF THEM. Its own note two hundred
      // lines up says so: it "mints the account and the room ITSELF rather than going through
      // signup". So no /talk arrival has ever carried the stash, and one navigation later her own
      // conversation could not tell she came through the funnel: the Ask Lala page's arrival read
      // stayed behind its guard, `free.payload` stayed null, and the plum window and the meter that
      // hang off it never painted. Walked on production: with the stash present the same page comes
      // up `ask-lala-main is-talk` with "5 more with me, love" on it.
      //
      // It is the SAME TAG this call already posts one line down, written through the SAME law that
      // refuses anything not on her roster (R28/R29), so the page and the row cannot disagree about
      // which door she came in by.
      writeLandingArrival(safeSessionStorage(), { tag: TALK_TAG });
      const create = await fetch("/api/room/create", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          name: form.firstName.trim(),
          // >>> **THE ROOM DOOR READS T2-2's TWO FIELDS, AND THIS SENT A THIRD ONE NOBODY READS
          // (2026-08-24).** <<< `handleRoomCreate` asks `consentViolations({ accepted:
          // body.termsAccepted === true, version: body.termsVersion })` — so `consent: true` was a
          // key the law never looks at, and **EVERY submission of her capture answered 403
          // `terms-consent-required`**: the account was made, the room never was, and she was left
          // on a page with no chart. That is her whole report, and it had no symptom in any source
          // fixture because the capture's OWN tickbox is real and its own verdict said READY.
          //
          // **THE VERSION IS THE ONE DECLARATION** (`src/termsConsent.js`), never a literal:
          // `consentViolations` compares against `TERMS_VERSION` and a second spelling is a door
          // that refuses her the day she re-words the Terms (R28/R29).
          termsAccepted: true,
          termsVersion: TERMS_VERSION,
          consent: true,
          // >>> HER LANDING TAG, AND ITS ABSENCE IS WHY OPENING NIGHT WAS UNMEASURABLE. <<< This
          // capture mints the account and the room ITSELF rather than going through signup, so the
          // one call site that carried a tag was never on this path -- 1,700 emails landed here and
          // not one signup could be told from an organic one. It is a CLAIM like every other tag:
          // the roster inside `recordLandingTag` decides whether it names one of her own doors, and
          // the write is WRITE-ONCE, so a member who arrived by another door first keeps hers.
          landingTag: TALK_TAG,
          chart,
        }),
      });
      const createBody = await create.json().catch(() => null);
      if (!create.ok || (createBody && createBody.ok === false)) {
        throw new Error((createBody && createBody.message) || "");
      }
      // >>> **ITEM 2 CLAUSE 1 — THE WINDOW WAITS FOR THE CHART.** <<<
      //
      // `/api/room/create` answers the instant the row exists and casts DETACHED (N18-1's own
      // ruling, and it is right: two narrative engines take well past a safe HTTP timeout). So
      // `onArrive` fired on a room that existed and a chart that did not, and she met a greeting
      // holding a chart nobody had. **Her glowing state was already the right screen; what was
      // missing is that it did not WAIT.**
      //
      // It polls the answer this page was going to ask for anyway (R28/R29: no second door, no
      // second reader), and every tick's decision is `chartPollVerdict`'s rather than an `if`
      // written here, so a gate RUNS all three outcomes with no browser.
      let waited = 0;
      for (;;) {
        let answered = false;
        let casting = true;
        try {
          const look = await fetch("/api/lala/messages", { headers: { accept: "application/json" } });
          const body = await look.json().catch(() => null);
          // R39/R47: read the PAYLOAD. A read that did not land is not an answer (R18/R65), and
          // treating it as one opens the window on a chart nobody confirmed.
          if (look.ok && body && body.ok !== false) { answered = true; casting = body.chartCasting === true; }
        } catch { /* a blip is a WAIT, never an opening */ }
        const verdict = chartPollVerdict({ answered, casting, elapsedMs: waited });
        if (verdict !== "wait") break;
        await new Promise((r) => setTimeout(r, CHART_POLL_MS));
        waited += CHART_POLL_MS;
      }
      onArrive(chart);
    } catch (err) {
      // R83: the server's own words come first, and the house's sentence is the floor.
      const said = err instanceof Error ? err.message : "";
      setError(said || TALK_CAPTURE_COPY.failed);
      setBusy(false);
      inFlight.current = false;
      // >>> RELEASED ON THE FAILURE ARM ONLY, AND THAT ASYMMETRY IS THE POINT. <<< A success
      // NAVIGATES, so releasing there would hand the page back to the door for the frames between
      // the answer and the landing — the very flash this exists to prevent. A failure leaves her on
      // this page with her form and her sentence, which is where she has to be.
      onCapturing?.(false);
    }
  }

  // >>> HER GLOWING GENERATING STATE. It is its OWN render rather than a badge on the form, because
  // a form still on the screen invites a second submit on the one page where that is a second
  // account. <<<
  if (state === "working") {
    return (
      <section className="talk-capture talk-capture--working" data-talk="working">
        <h1 className="talk-capture__mark">{TALK_CAPTURE_COPY.heading}</h1>
        <div className="talk-glow" aria-hidden="true"><span /><span /><span /></div>
        {/* >>> **HER STATUS LINE, AND IT IS THE ONE THING ON THIS SCREEN THAT NEVER CHANGES.** <<<
            The cast really takes about eighty seconds (measured: today's median is 81s), and the
            rotating lines below are ADVERTISING — not one of them says her chart is being pulled
            right now. So the status sits ABOVE them, persistent, present tense, and it names what
            will happen next so nobody decides the page has frozen and leaves.

            `role="status"` rather than another `aria-live` region: a screen reader announces it once
            when it arrives and is not re-interrupted by it every time the line underneath rotates. */}
        <p className="talk-capture__status" role="status">{TALK_CAPTURE_COPY.waitStatus}</p>
        {/* >>> HER INFLUENCE LINE. The `key` is what makes the fade a FADE: React remounts the
            paragraph when the line changes, so the CSS animation runs again instead of the text
            swapping under a static element. `aria-live="polite"` stays — a screen reader hears each
            line as it arrives rather than being interrupted mid-sentence. */}
        <p
          className="talk-capture__stage talk-capture__influence"
          data-talk-influence={Math.floor(Math.max(0, now - startedAt) / TALK_STAGE_MS)}
          key={talkInfluenceAt(Math.max(0, now - startedAt), influence, TALK_STAGE_MS)}
          aria-live="polite"
        >
          {talkInfluenceAt(Math.max(0, now - startedAt), influence, TALK_STAGE_MS)}
        </p>
      </section>
    );
  }

  return (
    <section className="talk-capture" data-talk="capture">
      <h1 className="talk-capture__mark">{TALK_CAPTURE_COPY.heading}</h1>
      <p className="talk-capture__sub">{TALK_CAPTURE_COPY.sub}</p>
      <p className="talk-capture__invitation">{TALK_CAPTURE_COPY.invitation}</p>

      <form className="talk-capture__form" onSubmit={submit} noValidate>
        <label className="talk-capture__field">
          <span>First name</span>
          <input
            type="text" name="firstName" autoComplete="given-name"
            value={form.firstName} onChange={(ev) => set("firstName", ev.target.value)}
          />
        </label>

        <label className="talk-capture__field">
          <span>Email</span>
          <input
            type="email" name="email" autoComplete="email"
            value={form.email} onChange={(ev) => set("email", ev.target.value)}
          />
        </label>

        <label className="talk-capture__field">
          <span>{t(DEFAULT_LANGUAGE, "form.birthDate")}</span>
          <input
            type="date" name="birthDate"
            value={form.birthDate} onChange={(ev) => set("birthDate", ev.target.value)}
          />
        </label>

        <div className="talk-capture__field">
          <span>{t(DEFAULT_LANGUAGE, "form.birthTime")}</span>
          <div className="talk-capture__time">
            <input
              type="time" name="birthTime" disabled={form.birthTimeUnknown}
              value={form.birthTime} onChange={(ev) => set("birthTime", ev.target.value)}
            />
          </div>
          {/* Her *with Unknown* clause, through the ONE control this house asks it with (R12-1). */}
          <UnknownTimeCheck
            checked={form.birthTimeUnknown}
            onChange={(next) => setForm((f) => ({ ...f, birthTimeUnknown: next, birthTime: next ? "" : f.birthTime }))}
            label={t(DEFAULT_LANGUAGE, "form.birthTimeUnknown")}
          />
        </div>

        <label className="talk-capture__field talk-capture__field--place">
          <span>Where you were born</span>
          <input
            type="text" name="birthplace" autoComplete="off"
            value={form.birthplace} onChange={(ev) => typePlace(ev.target.value)}
          />
        </label>
        {placeRows.length ? (
          <ul className="talk-capture__places">
            {placeRows.slice(0, 6).map((row, i) => (
              <li key={`${row.place_name}-${i}`}>
                <button type="button" onClick={() => pickPlace(row)}>
                  {row.formatted || [row.place_name, row.state, row.country].filter(Boolean).join(", ")}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {placeLookup === "searching" ? <p className="talk-capture__note">Looking...</p> : null}

        <label className="talk-capture__consent">
          <input type="checkbox" checked={form.consent} onChange={(ev) => set("consent", ev.target.checked)} />
          <span>{TALK_CAPTURE_COPY.consent}</span>
        </label>

        {error ? <p className="talk-capture__error" role="alert">{error}</p> : null}

        <button type="submit" className="talk-capture__go" data-talk-submit disabled={!verdict.ready}>
          {TALK_CAPTURE_COPY.action}
        </button>
      </form>

      <p className="talk-capture__safety">{TALK_CAPTURE_COPY.safety}</p>

      {/* Her review carousel, through the ONE rail the landing and the membership page already read
          — so REV-1's approval gate, her DM privacy law, the reduced-motion ruling and the
          silent-failure law all reach this page by EXISTING (R28/R29). */}
      <ReviewRail rows={rows} place="talk" />
    </section>
  );
}
